import React from 'react'
import PropTypes from 'prop-types'
import {
  Page, View, Document, StyleSheet, Font
} from '@react-pdf/renderer'
import Info from '@generators/pdfComponents/icons/Info'
import colors from '@settings/styleSettings'
import InterRegular from '@assets/fonts/Inter-Regular.ttf'
import InterBold from '@assets/fonts/Inter-Bold.ttf'
import { getRecommendations } from '@util/recommendationUtil'
import { getDateFromStringDate } from '@util/timeUtil'
import sexEnum from '@util/sexEnum'
import {
  getHdlC,
  getIfIsDiabetic,
  getIfIsDyslipidemic,
  getIfIsHypertensive,
  getImc,
  getRcq,
  getBodyWater,
  getBoneMassEstimative,
  getProtein,
  getIdealWeight,
} from '@util/equationsUtil'
import { cpfShowMask, insuranceMask, oneDecimalMask } from '@util/stringUtil'
import { Item } from './pdfComponents/Item'
import { Line } from './pdfComponents/Line'
import { MainTitle } from './pdfComponents/MainTitle'
import { ActionTitle } from './pdfComponents/ActionTitle'
import { RiskClassification } from './pdfComponents/RiskClassification'
import { Important } from './pdfComponents/Important'
import { HealthCharacteristic } from './pdfComponents/HealthCharacteristic'
import { BloodPressure } from './pdfComponents/BloodPressure'
import { CongratulationsText } from './pdfComponents/CongratulationsText'
import { Footer } from './pdfComponents/Footer'
import { SecondaryTitle } from './pdfComponents/SecondaryTitle'
import { NoRiskRecommendation } from './pdfComponents/NoRiskRecommendation'
import { Observation } from './pdfComponents/Observation'
import InfoLine from './pdfComponents/InfoLine'

import InsurerEnum from '@util/InsurerEnum'

Font.register({ family: 'Inter-Regular', src: InterRegular })
Font.register({ family: 'Inter-Bold', src: InterBold })

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#FFFFFF',
    marginVertical: 10,
  },
  section: {
    margin: '10 0',
  },
  container: {
    margin: '0 24',
    width: '100%',
  },
  firstPart: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
})

// Create Document Component
const MyDocument = ({ reportData, insurance }) => {
  const insurer = InsurerEnum[insurance]
  const info = (color) => <Info color={color} />
  const {
    name, age, insuranceNumber, sex, birthdate, cpf
  } = reportData
  const lastCollection = reportData.lastGeneralCollection || {}
  const {
    collectedAt,
    height,
    weight,
    abdominalCircumference,
    waistCircumference,
    hipCircumference,
    fat,
    visceralFat,
    leanMass,
    calories,
    triglycerides,
    glucose,
    hdlCholesterol,
    ldlCholesterol,
    vldlCholesterol,
    systBloodPressure,
    diastBloodPressure,
    hasSystemicArterialHypertension,
    hasDiabetes,
    totalCholesterol,
    notHdl,
    observation,
  } = lastCollection

  // Calculated Metrics Logic (Ported from ReportResult)
  let bodyWater = 0
  let boneMass = 0
  let protein = 0

  if (leanMass && Number(leanMass) > 0 && weight) {
    bodyWater = Number(getBodyWater(leanMass, weight))
    boneMass = Number(getBoneMassEstimative(leanMass))
    protein = Number(getProtein(leanMass, weight))
  }

  const imc = getImc(weight, height)
  const rcq = getRcq(waistCircumference, hipCircumference)
  const hdlC = notHdl || getHdlC(totalCholesterol, hdlCholesterol)
  const isHypertensive = getIfIsHypertensive(
    systBloodPressure,
    diastBloodPressure,
    hasSystemicArterialHypertension
  )
  const isDiabetic = getIfIsDiabetic(glucose, hasDiabetes)
  const isDyslipidemic = getIfIsDyslipidemic(
    totalCholesterol,
    hdlCholesterol,
    triglycerides,
    ldlCholesterol
  )
  const idealWeight = height ? getIdealWeight(height) : 0
  const {
    imediate, nextMonths, nextYears, stageOne, stageTwo
  } = getRecommendations(reportData)

  const renderDetails = () => (
    <View style={styles.firstPart}>
      <View style={styles.section} wrap={false}>
        <Item
          label="Nome"
          text={name}
          textColor={insurer.id === InsurerEnum['unimed-assis'].id ? colors.strongGreen : colors.strongRed}
          width={560}
        />
      </View>
      <View style={[styles.section, { width: 161 }]} wrap={false}>
        <Item label="CPF" text={cpfShowMask(cpf)} />
      </View>
      <View style={[styles.section, { width: 161 }]} wrap={false}>
        <Item label="Sexo" text={sexEnum[sex].label} />
      </View>
      <View style={[styles.section, { width: 161 }]} wrap={false}>
        <Item
          label="Carteirinha"
          text={insuranceMask(insuranceNumber) || 'Não informado'}
        />
      </View>
      <View style={[styles.section, { width: 161 }]} wrap={false}>
        <Item
          label="Data de nasc."
          text={getDateFromStringDate(birthdate) || 'Não informado'}
        />
      </View>
      <View style={[styles.section, { width: 161 }]} wrap={false}>
        <Item label="Idade" text={age ? `${age} anos` : 'Não informado'} />
      </View>
      <View style={styles.section} wrap={false}>
        <Item
          label="Data da coleta"
          text={getDateFromStringDate(collectedAt)}
        />
      </View>
    </View>
  )

  const renderMeasurements = () => (
    <>
      <SecondaryTitle text="Medidas antropométricas" />
      <View style={styles.firstPart}>
        <View style={styles.section} wrap={false}>
          <Item
            label="Altura"
            text={`${oneDecimalMask(height)} cm`}
            width={161}
          />
        </View>
        <View style={styles.section} wrap={false}>
          <Item
            label="Peso"
            text={`${oneDecimalMask(weight)} kg`}
            width={161}
          />
        </View>
        <View style={[styles.section, { width: 0 }]} wrap={false}>
          {Number(idealWeight) > 0 ? (
            <Item label="Peso Ideal" text={`${idealWeight} kg`} width={161} />
          ) : (
            <Item label="Peso Ideal" text="-" width={161} />
          )}
        </View>
        <View style={styles.section} wrap={false}>
          <Item
            label="Circ. abdominal"
            text={
              abdominalCircumference
                ? `${oneDecimalMask(abdominalCircumference)} cm`
                : 'Não coletado'
            }
            width={161}
          />
        </View>
        <View style={styles.section} wrap={false}>
          <Item
            label="Circ. cintura"
            text={
              waistCircumference
                ? `${oneDecimalMask(waistCircumference)} cm`
                : 'Não coletado'
            }
            width={161}
          />
        </View>
        <View style={[styles.section, { width: 0 }]} wrap={false}>
          <Item
            label="Circ. quadril"
            text={
              hipCircumference
                ? `${oneDecimalMask(hipCircumference)} cm`
                : 'Não coletado'
            }
            width={161}
          />
        </View>
      </View>
      <Line lineColor={colors.softGray} style={{ margin: '32 0 12 0' }} />
      <View style={styles.section} wrap={false}>
        <HealthCharacteristic type="imc" value={imc} />
        {waistCircumference > 0 && hipCircumference > 0 && (
          <HealthCharacteristic type="rcq" value={rcq} />
        )}
      </View>
    </>
  )

  const renderNutritionalAssessment = () => (
    <View style={styles.section} wrap={false}>
      <SecondaryTitle text="Aferição nutricional" />
      {fat && <HealthCharacteristic type="fat" value={fat} />}
      {visceralFat && (
        <HealthCharacteristic type="visceralFat" value={visceralFat} />
      )}
      {leanMass && (
        <HealthCharacteristic type="muscleMass" value={leanMass} />
      )}
      {calories && (
        <HealthCharacteristic type="calories" value={calories} />
      )}
      {bodyWater > 0 && (
        <HealthCharacteristic type="bodyWater" value={bodyWater} />
      )}
      {boneMass > 0 && (
        <HealthCharacteristic type="boneMass" value={boneMass} />
      )}
      {protein > 0 && (
        <HealthCharacteristic type="protein" value={protein} />
      )}
    </View>
  )

  const renderLaboratorialData = () => (
    <View style={styles.section}>
      <SecondaryTitle text="Exames laboratoriais" />

      <InfoLine
        text="Beneficiário com diabetes mellitus informada no momento da coleta dos dados?"
        informed={hasDiabetes}
      />

      {glucose > 0 && (
        <HealthCharacteristic type="glucose" value={glucose} />
      )}

      <HealthCharacteristic type="totalCholesterol" value={totalCholesterol} />

      {hdlCholesterol > 0 && (
        <HealthCharacteristic type="hdl" value={hdlCholesterol} />
      )}

      {hdlC > 0 && (
        <HealthCharacteristic type="hdl-c" value={hdlC} />
      )}

      {ldlCholesterol > 0 && (
        <HealthCharacteristic type="ldl" value={ldlCholesterol} />
      )}

      {vldlCholesterol > 0 && (
        <HealthCharacteristic type="vldlCholesterol" value={vldlCholesterol} />
      )}

      {triglycerides > 0 && (
        <HealthCharacteristic type="triglycerides" value={triglycerides} />
      )}
    </View>
  )

  const renderRecomendations = () => (
    <>
      <View wrap={false}>
        <SecondaryTitle text="Recomendações de tratamento" />
        {imediate && imediate.length && (
          <View style={styles.section} wrap={false}>
            <ActionTitle
              titleText="Ações Imediatas"
              titleColor={colors.strongRed}
              recommendations={imediate}
            />
          </View>
        )}
      </View>
      {nextMonths && nextMonths.length && (
        <View style={styles.section} wrap={false}>
          <ActionTitle
            titleText="Ações para os próximos meses"
            titleColor={colors.strongYellow}
            recommendations={nextMonths}
          />
        </View>
      )}
      {nextYears && nextYears.length && (
        <View style={styles.section} wrap={false}>
          <ActionTitle
            titleText="Ações para os próximos anos"
            titleColor={colors.strongGreen}
            recommendations={nextYears}
          />
        </View>
      )}
      {stageOne && stageOne.length && (
        <View style={styles.section} wrap={false}>
          <ActionTitle
            titleColor={colors.strongBlue}
            exclamationBackGround="#00995C1A"
            titleText={`Ações complementares com base na idade (acima de ${sexEnum[sex].ageLimit} anos) - Etapa 1`}
            recommendations={stageOne}
          />
        </View>
      )}
      {stageTwo && stageTwo.length && (
        <View style={styles.section} wrap={false}>
          <ActionTitle
            titleText={`Ações complementares com base na idade (acima de ${sexEnum[sex].ageLimit} anos) - Etapa 2`}
            titleColor={colors.strongBlue}
            recommendations={stageTwo}
          />
        </View>
      )}
      { (imediate && nextMonths && nextYears && stageOne && stageTwo)
        && !imediate.length
        && !nextMonths.length
        && !nextYears.length
        && !stageOne.length
        && !stageTwo.length && <NoRiskRecommendation />}
    </>
  )

  const renderFooter = () => (
    <>
      <View style={styles.section} wrap={false}>
        <Line lineColor={colors.softGray} />
        <Important icon={info} date={getDateFromStringDate(collectedAt)} />
        <Line lineColor={colors.softGray} />
      </View>
      <View style={styles.section} wrap={false}>
        <CongratulationsText />
      </View>
      <View style={styles.section} wrap={false}>
        <Footer />
      </View>
    </>
  )

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <MainTitle insurer={insurer} />
          <Line lineColor={colors.softGray} />
          {renderDetails()}
          {glucose ? (
            <>
              <Line lineColor={colors.softGray} />
              <View style={styles.section} wrap={false}>
                <RiskClassification
                  has={isHypertensive}
                  dm={isDiabetic}
                  cholesterol={isDyslipidemic}
                  imc={imc}
                />
              </View>
              <Line lineColor={colors.softGray} style={{ marginBottom: 24 }} />
            </>
          ) : (
            <Line lineColor={colors.softGray} style={{ marginBottom: 24 }} />
          )}
          {renderMeasurements()}
          {renderNutritionalAssessment()}

          <View style={styles.section} wrap={false}>
            <SecondaryTitle text="Pressão arterial" />
            <BloodPressure
              syst={systBloodPressure}
              diast={diastBloodPressure}
            />
            <InfoLine
              text="Beneficiário com hipertensão informada no momento da coleta dos dados?"
              informed={hasSystemicArterialHypertension}
            />
          </View>

          {totalCholesterol > 0 && (
            renderLaboratorialData()
          )}

          {observation && (
            <Observation value={observation} />
          )}

          {totalCholesterol > 0 && (
            renderRecomendations()
          )}

          {insurer.id === InsurerEnum['unimed-assis'].id ? renderFooter() : null}
        </View>
      </Page>
    </Document>
  )
}

MyDocument.propTypes = {
  reportData: PropTypes.shape({
    name: PropTypes.string,
    age: PropTypes.number,
    insuranceNumber: PropTypes.string,
    cpf: PropTypes.string,
    sex: PropTypes.string,
    birthdate: PropTypes.string,
    lastGeneralCollection: PropTypes.shape({
      collectedAt: PropTypes.string,
      height: PropTypes.number,
      weight: PropTypes.number,
      abdominalCircumference: PropTypes.number,
      waistCircumference: PropTypes.number,
      hipCircumference: PropTypes.number,
      fat: PropTypes.number,
      visceralFat: PropTypes.number,
      leanMass: PropTypes.number,
      calories: PropTypes.number,
      triglycerides: PropTypes.number,
      glucose: PropTypes.number,
      hdlCholesterol: PropTypes.number,
      ldlCholesterol: PropTypes.number,
      vldlCholesterol: PropTypes.number,
      systBloodPressure: PropTypes.number,
      diastBloodPressure: PropTypes.number,
      totalCholesterol: PropTypes.number,
      notHdl: PropTypes.number,
      hasSystemicArterialHypertension: PropTypes.bool,
      hasDiabetes: PropTypes.bool,
      observation: PropTypes.string,
    }),
  }).isRequired,
  insurance: PropTypes.string.isRequired,
}

MyDocument.defaultProps = {
}

export default MyDocument
